import type { Service } from "./services";
import type { SocialPost } from "./social";

/**
 * The Work section's shapes and helpers.
 *
 * The clients and what the studio did for each are curated in lib/clients.ts and shaped by
 * lib/work.server.ts. Each client gets a card in the work section, a sheet over it, and a
 * page at /work/<slug>.
 */

/**
 * Legacy. The drawn placeholder faces were retired on 2026-08-31; the keys stay only so the
 * Work collection's existing `faceKey` column keeps its select options until it is migrated
 * away. Nothing renders them.
 */
export const FACE_KEYS = [
  "poster",
  "social",
  "video",
  "dashboard",
  "appsheet",
  "dealroom",
] as const;

/**
 * How artwork sits in a frame, stills and clips alike. "cover" fills it and crops the
 * overflow, right for a screenshot or a widescreen clip. "contain" fits the whole thing on
 * a mat, which is what a portrait poster or a vertical reel needs so it is not sliced down
 * to its middle band.
 */
export const FITS = ["cover", "contain"] as const;

export type Fit = (typeof FITS)[number];

/** One still: an uploaded file's URL or a path under /public, with what the CMS knows of it. */
export type Shot = {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  /** The editor's focal point, as percentages, so a crop keeps the part that matters. */
  focalX?: number;
  focalY?: number;
};

/** One business problem a system solved. */
export type Story = { problem: string; built: string; result: string };

/** What a deliverable is, which decides how its tab in the client view is drawn. */
export type DeliverableKind = "website" | "app" | "erp" | "build" | "content" | "instagram" | "linkedin";

/**
 * One thing the studio did for a client: their website, a build that is not a public site,
 * a run of content, or the Instagram or LinkedIn content it made.
 */
export type Deliverable = {
  /** Stable within a client, for tab values and keys. */
  id: string;
  kind: DeliverableKind;
  /** Shown on the card chip and the tab: "Website", "Data Room · AppSheet", "Instagram". */
  label: string;
  /** A live site, or the feed's own page. */
  url?: string;
  shots: Shot[];
  video?: string;
  fit?: Fit;
  /** Posts and profiles, for the two social kinds. */
  posts: SocialPost[];
  /** What the studio did, in a sentence or two. */
  summary?: string;
  /**
   * For work shown without its screens (an ERP, a private platform): the business problems
   * it solved, each as problem, what we built, and what changed. Plain language, no client
   * data, no numbers that are not public.
   */
  story?: Story[];
  /** Extra links, e.g. both app stores. */
  links?: { label: string; href: string }[];
};

/** One client, with everything the studio did for them. */
export type Client = {
  /** Stable address for /work/<slug>, derived from the name. */
  slug: string;
  name: string;
  /** One line on what the studio did, from the first paper that has one. */
  blurb?: string;
  /** The live site, when there is one. */
  url?: string;
  /** The artwork that stands for the client on its card. */
  cover?: Shot;
  /** The services the work falls under, in the order of lib/services.ts. */
  services: Pick<Service, "id" | "title" | "kicker" | "description">[];
  deliverables: Deliverable[];
};

/** Host without the www, for "Live at ximenaavsaenz.com". Null for anything unparseable. */
export const displayHost = (url: string): string | null => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
};

/** CSS object-position for a shot, from its focal point; top for a tall board, else centre. */
export const objectPosition = (shot: Shot | undefined): string => {
  if (shot?.focalX != null && shot?.focalY != null) return `${shot.focalX}% ${shot.focalY}%`;
  if (shot?.width && shot?.height && shot.height > shot.width) return "50% 0%";
  return "50% 50%";
};
